class ApexSwiper {
  #swiper;
  #wrapper;
  #scorll;
  #slides;
  #groupCount;
  #pagination;
  #bullets;
  #prevButton;
  #nextButton;
  #scrollAnimated;
  #currentIndex = 0;
  #limit = 1;
  #startX = 0;
  #isDragging = false;
  #transition;
  #isDesktop = window.innerWidth > 768;
  #hasButtons;
  #autoScrollTimer;
  #locked = false;

  constructor(swiperElement, initOptions = {}) {
    if (!swiperElement || !swiperElement.classList.contains("apex-swiper")) {
      throw new Error("请传入一个包含类名 apex-swiper 的 DOM 元素");
    }

    this.#swiper = swiperElement;
    this.#wrapper = this.#swiper.querySelector(".apex-swiper-wrapper");
    this.#scorll = this.#swiper.querySelector(".apex-swiper-scroll");
    this.#slides = Array.from(this.#swiper.querySelectorAll(".apex-swiper-slide"));
    this.#transition = window.getComputedStyle(this.#scorll).transition;
    this.#wrapper.style['touch-action'] = "pan-y";

    // 初始化
    this.#init(initOptions);
  }

  #init({ mobileOnly = false, autoplay = true, hasButtons = true, middleButtons = false }) {
    if (mobileOnly && this.#isDesktop) {
      return;
    }
    this.#calculateLimit();
    if (this.#groupCount < 2) {
      return;
    }
    this.#hasButtons = hasButtons;
    this.#createDuplicateElementsWithLimit();
    if (this.#groupCount > 2) {
      this.#moveElemtsToFirstOrLast(true);
      this.#setScrollTranslateToSecond();
    }
    this.#createBullets();
    hasButtons && this.#createOperationButtons();
    autoplay && this.#createScrollAnimated();
    if (hasButtons && middleButtons) {
      this.#middleControlButtons();
    }
    this.#updateVisibility();
    this.#bindEvents();
    autoplay && this.#autoScroll();
    this.#updateActive(this.#groupCount === 2 ? '0' : '');
  }

  /** 根据wrapper和slides的宽度，计算limit的值 */
  #calculateLimit() {
    const wrapperWidth = this.#wrapper.getBoundingClientRect().width;
    const slideWidth = this.#slides[0].getBoundingClientRect().width;
    const limit = Math.floor(wrapperWidth / slideWidth);
    this.#limit = limit;
    this.#groupCount = Math.ceil(this.#slides.length / this.#limit);
  }

  #getActualSlides() {
    return Array.from(this.#swiper.querySelectorAll(".apex-swiper-slide"));
  }

  #setScrollTranslateToSecond() {
    const scrollLeft = this.#getScrollAreaWidth();
    this.#scorll.style.transition = "none";
    this.#scorll.style.transform = `translateX(${-scrollLeft}px)`;
    setTimeout(() => {
      this.#scorll.style.transition = this.#transition;
    }, 10);
  }

  /** 根据slides的数量创建bullet */
  #createBullets() {
    const fragment = document.createElement("div");
    fragment.classList.add("apex-swiper-pagination");
    for (let i = 0; i < this.#groupCount; i++) {
      const bullet = document.createElement("span");
      bullet.classList.add("swiper-pagination-bullet");
      fragment.appendChild(bullet);
    }
    this.#swiper.appendChild(fragment);
    this.#pagination = fragment;
    this.#bullets = Array.from(this.#swiper.querySelectorAll(".swiper-pagination-bullet"));
  }

  /** 创建切换按钮 */
  #createOperationButtons() {
    const prevButton = document.createElement("button");
    prevButton.classList.add("circle-button", "circle-button--xs", "circle-button--fill");
    prevButton.setAttribute("is", "prev-button");
    prevButton.innerHTML = '<span class="animated-arrow animated-arrow--reverse"></span>';
    this.#swiper.appendChild(prevButton);
    this.#prevButton = prevButton;
    const nextButton = document.createElement("button");
    nextButton.classList.add("circle-button", "circle-button--xs", "circle-button--fill");
    nextButton.setAttribute("is", "next-button");
    nextButton.innerHTML = '<span class="animated-arrow"></span>';
    this.#swiper.appendChild(nextButton);
    this.#nextButton = nextButton;
  }

  /** 如果slides除以limit有余数，则补全slides */
  #createDuplicateElementsWithLimit() {
    const slides = this.#getActualSlides();
    if (slides.length % this.#limit === 0) return;
    for (let i = 0; i < slides.length % this.#limit; i++) {
      const emptySlide = document.createElement("div");
      emptySlide.classList.add("apex-swiper-slide");
      this.#scorll.appendChild(emptySlide);
    }
  }

  /** 将limit个元素移动到最前面或者最后面 */
  #moveElemtsToFirstOrLast(isFirst = true) {
    const slides = this.#getActualSlides();
    const elements = isFirst ? slides.slice(-this.#limit) : slides.slice(0, this.#limit);
    const fragment = document.createDocumentFragment();
    elements.forEach((el) => fragment.appendChild(el));
    if (isFirst) {
      this.#scorll.insertBefore(fragment, this.#scorll.firstChild);
    } else {
      this.#scorll.appendChild(fragment);
    }
  }

  #middleControlButtons() {
    const firstImg = this.#slides[0].querySelector("img");
    if (!firstImg) {
      return;
    }
    const imgHeight = firstImg.getBoundingClientRect().height;
    this.#prevButton.style.top = `${imgHeight / 2}px`;
    this.#nextButton.style.top = `${imgHeight / 2}px`;
  }

  #createScrollAnimated() {
    const animatedSpan = document.createElement("span");
    animatedSpan.classList.add("scroll-animated");
    this.#swiper.appendChild(animatedSpan);
    this.#scrollAnimated = animatedSpan;
  }

  #updateVisibility() {
    if (!this.#prevButton || !this.#nextButton) {
      return;
    }
    if (this.#slides.length <= this.#limit) {
      this.#pagination.style.display = "none";
      this.#prevButton.style.display = "none";
      this.#nextButton.style.display = "none";
    } else {
      this.#pagination.style.display = "";
      this.#prevButton.style.display = "";
      this.#nextButton.style.display = "";
    }
  }

  #getScrollAreaWidth() {
    const slideWidth = this.#slides[0].getBoundingClientRect().width;
    return this.#limit * slideWidth;
  }

  #handleTouchStart(e) {
    this.#isDragging = true;
    this.#startX = e.touches ? e.touches[0].clientX : e.x;
    this.#autoScroll(false); // 停止自动轮播
    this.#scorll.style.transition = "none";
  }

  #handleTouchMove(e) {
    // e.preventDefault();
    if (!this.#isDragging) return;

    const currentX = e.touches ? e.touches[0].clientX : e.x;
    const diff = currentX - this.#startX;
    const newTranslate = -this.#getScrollAreaWidth() + diff;

    if (Math.abs(diff) > 50) {
      e.preventDefault();
    }

    // 限制滑动范围
    if (newTranslate < -this.#getScrollAreaWidth() * (this.#groupCount - 1) || newTranslate > 0) {
      return;
    }

    this.#scorll.style.transform = `translateX(${newTranslate}px)`;

    // 判断是否超过一半宽度
    const slideWidth = this.#getScrollAreaWidth();
    if (Math.abs(diff) > slideWidth / 2) {
      const newIndex = diff > 0 ? 0 : 2;
      if (newIndex >= 0 && newIndex < this.#groupCount) {
        this.#updateActive(newIndex);
      }
    }
  }

  #handleTouchEnd(e) {
    if (!this.#isDragging) return;

    const endX = e.changedTouches ? e.changedTouches[0].clientX : e.x;
    const diff = endX - this.#startX;
    const slideWidth = this.#getScrollAreaWidth();

    let newIndex = 1;
    if (Math.abs(diff) > Math.max(slideWidth * 0.1, 50)) {
      newIndex = diff > 0 ? 0 : 2;
    }

    this.#isDragging = false;
    this.#scorll.style.transition = this.#transition;
    this.scrollToIndex(newIndex);
    if (!this.#isDesktop) {
      this.#autoScroll(); // 重新开启自动轮播
    }
  }

  #bindTouchEvents() {
    // 如果是pc端，兼容鼠标事件
    if (this.#isDesktop) {
      this.#swiper.addEventListener("mouseenter", () => {
        this.#autoScroll(false);
      });
      if (!this.#hasButtons) {
        this.#swiper.addEventListener("mousedown", this.#handleTouchStart.bind(this));
        this.#swiper.addEventListener("mousemove", this.#handleTouchMove.bind(this));
        this.#swiper.addEventListener("mouseup", this.#handleTouchEnd.bind(this));
      }
      this.#swiper.addEventListener("mouseleave", () => {
        this.#autoScroll();
      });
    } else {
      this.#swiper.addEventListener("touchstart", this.#handleTouchStart.bind(this));
      this.#swiper.addEventListener("touchmove", this.#handleTouchMove.bind(this));
      this.#swiper.addEventListener("touchend", this.#handleTouchEnd.bind(this));
    }
  }

  #bindEvents() {
    if (this.#prevButton) {
      this.#prevButton.addEventListener("touchstart", (e) => e.stopPropagation(), { passive: false });
      this.#prevButton.addEventListener("click", () => this.scrollToIndex(0));
    }
    if (this.#nextButton) {
      this.#nextButton.addEventListener("touchstart", (e) => e.stopPropagation(), { passive: false });
      this.#nextButton.addEventListener("click", () => this.scrollToIndex(2));
    }
    this.#bindTouchEvents();
    // this.#bullets.forEach((bullet, index) => {
    //   bullet.addEventListener("click", () => this.scrollToIndex(index));
    // });

    if (this.#groupCount > 1 && this.#scrollAnimated) {
      this.#scrollAnimated.addEventListener("animationiteration", () => {
        if (this.#isDragging) return;
        this.scrollToIndex(2);
      });
    }
  }

  #autoScroll(enable = true) {
    if (this.#scrollAnimated) {
      if (enable) {
        this.#autoScrollTimer = setTimeout(() => {
          this.#scrollAnimated.style.animationPlayState = "running";
        }, 3000);
      } else {
        this.#autoScrollTimer && clearTimeout(this.#autoScrollTimer);
        this.#scrollAnimated.style.animationPlayState = "paused";
      }
    }
  }

  scrollToIndex(index) {
    if (this.#locked) return;
    this.#locked = true;
    const isDouble = this.#groupCount == 2;
    if (index == 0) {
      let newIndex = this.#currentIndex - 1;
      this.#currentIndex = newIndex < 0 ? this.#groupCount - 1 : newIndex;
      if (isDouble) {
        index = this.#currentIndex;
      }
    } else if (index == 2) {
      let newIndex = this.#currentIndex + 1;
      this.#currentIndex = newIndex > this.#groupCount - 1 ? 0 : newIndex;
      if (isDouble) {
        index = this.#currentIndex;
      }
    }
    const scrollLeft = this.#getScrollAreaWidth();
    this.#scorll.style.transition = this.#transition;
    this.#scorll.style.transform = `translateX(${-scrollLeft * index}px)`;
    setTimeout(() => {
      if (index != 1 && !isDouble) {
        this.#setScrollTranslateToSecond();
        this.#moveElemtsToFirstOrLast(index < 1);
      }
      this.#updateActive(isDouble ? index + '' : '');
      this.#locked = false;
    }, 300);
  }

  #updateActive(newIndex) {
    const slides = this.#getActualSlides();
    slides.forEach((slide, index) => {
      slide.classList.toggle("active", newIndex ? index == newIndex : index >= this.#limit && index < this.#limit * 2);
    });

    this.#bullets.forEach((bullet, index) => {
      bullet.classList.toggle("active", index == this.#currentIndex);
    });
  }
}

window.ApexSwiper = ApexSwiper;
